import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUUpLeft, Plus, QrCode } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { BarcodeScanDialog } from "@/components/BarcodeScanDialog";
import { FilterBar } from "@/components/FilterBar";
import { api } from "@/lib/api";

export const RMA_STATUSES = [
  "Requested",
  "Approved",
  "Shipped",
  "Credited",
  "Replaced",
  "Refunded",
  "Rejected",
];

export const RMA_STATUS_STYLE = {
  Requested: "border-amber-500/25 bg-amber-500/10 text-amber-400",
  Approved: "border-indigo-400/25 bg-indigo-500/10 text-indigo-300",
  Shipped: "border-cyan-400/25 bg-cyan-500/10 text-cyan-300",
  Credited: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  Replaced: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  Refunded: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  Rejected: "border-red-500/25 bg-red-500/10 text-red-400",
};

export const rmaMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const rmaDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

export default function RMAs() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [scanOpen, setScanOpen] = useState(params.get("scan") === "1");
  const scanHandled = useRef(false);

  // a label QR carries the RMA link; a typed code like RMA-1001 is looked up instead
  const onScanned = useCallback(
    async (text) => {
      if (scanHandled.current || !text) return;
      scanHandled.current = true;
      try {
        const { data } = await api.get("/rmas/lookup", { params: { code: text } });
        setScanOpen(false);
        navigate(`/inventory/rmas/${data.id}?scan=1`);
      } catch {
        scanHandled.current = false;
        toast.error(`No return matches “${text.slice(0, 40)}”`);
      }
    },
    [navigate],
  );

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const [list, st] = await Promise.all([
      api.get("/rmas", { params }),
      api.get("/rmas/stats"),
    ]);
    setItems(list.data);
    setStats(st.data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const shown = items.filter(
    (r) => statusFilter.length === 0 || statusFilter.includes(r.status),
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Returns (RMA)" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="rmas-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Inventory
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Returns (RMA)
              </h1>
              <p className="mt-1 text-sm text-zinc-500" data-testid="rmas-summary">
                {stats.open || 0} open · {stats.total || 0} total ·{" "}
                {rmaMoney(stats.credit_total)} credited back
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="scan-rma-label-button"
                variant="outline"
                onClick={() => {
                  scanHandled.current = false;
                  setScanOpen(true);
                }}
                className="h-12 border-zinc-700 bg-transparent text-xs font-bold uppercase tracking-widest text-zinc-200 hover:bg-zinc-800"
              >
                <QrCode size={16} weight="bold" className="mr-2" /> Scan label
              </Button>
              <Button
                data-testid="add-rma-button"
                onClick={() => navigate("/inventory/rmas/new")}
                className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                <Plus size={15} weight="bold" className="mr-2" /> New Return
              </Button>
            </div>
          </div>
        </div>

        <BarcodeScanDialog
          open={scanOpen}
          onOpenChange={setScanOpen}
          onDetected={onScanned}
          title="Scan a return label"
          placeholder="RMA code, e.g. RMA-1001"
          testPrefix="rma-scan"
        />

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Open", stats.open || 0, "text-amber-400"],
            ["Shipped", stats.Shipped || 0, "text-cyan-300"],
            [
              "Closed",
              (stats.Credited || 0) +
                (stats.Replaced || 0) +
                (stats.Refunded || 0) +
                (stats.Rejected || 0),
              "text-emerald-400",
            ],
            ["Credited $", rmaMoney(stats.credit_total), "text-indigo-300"],
          ].map(([k, v, c]) => (
            <div
              key={k}
              data-testid={`rma-stat-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                {k}
              </div>
              <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
                {v}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6" data-testid="rmas-list">
          <div className="mb-4">
            <FilterBar
              testidPrefix="rmas"
              search={{
                value: q,
                onChange: setQ,
                placeholder: "Search code, supplier, RMA #, slab code…",
                testid: "rma-search",
              }}
              sections={[
                {
                  key: "status",
                  label: "Status",
                  multi: true,
                  values: statusFilter,
                  allValue: "All",
                  onChange: setStatusFilter,
                  options: ["All", ...RMA_STATUSES].map((f) => [
                    f,
                    f,
                    `rma-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
                  ]),
                },
              ]}
            />
          </div>

          {shown.length === 0 && (
            <div className="rounded-lg border border-dashed border-zinc-800 p-10 text-center">
              <ArrowUUpLeft
                size={26}
                weight="bold"
                className="mx-auto text-zinc-700"
              />
              <p className="mt-3 text-sm text-zinc-400">
                No returns yet. Start one when a slab or sink has to go back to the supplier.
              </p>
            </div>
          )}

          {shown.map((r) => (
            <Link
              key={r.id}
              to={`/inventory/rmas/${r.id}`}
              data-testid={`rma-card-${r.id}`}
              className="mt-2 block rounded-lg border border-zinc-800 bg-zinc-900 p-4 transition-colors duration-150 hover:border-zinc-700"
            >
              <div className="flex flex-wrap items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono-data text-xs text-yellow-500">
                      {r.code}
                    </span>
                    <span
                      data-testid={`rma-status-${r.id}`}
                      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                        RMA_STATUS_STYLE[r.status] ||
                        "border-zinc-700 bg-zinc-800 text-zinc-300"
                      }`}
                    >
                      {r.status}
                    </span>
                    {r.stock_removed && (
                      <span className="rounded-full border border-cyan-500/25 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cyan-300">
                        Out of stock
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1 truncate text-base font-bold">
                    {r.supplier || "No supplier"}
                  </h3>
                  <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400">
                    <span>
                      {(r.items || []).length} item
                      {(r.items || []).length === 1 ? "" : "s"}
                    </span>
                    {r.manufacturer_rma && (
                      <span>
                        <span className="text-zinc-600">Supplier RMA </span>
                        {r.manufacturer_rma}
                      </span>
                    )}
                    {r.po_number && (
                      <span>
                        <span className="text-zinc-600">PO </span>
                        {r.po_number}
                      </span>
                    )}
                    {r.reason && <span className="truncate">{r.reason}</span>}
                  </div>
                </div>
                <div className="text-right">
                  {r.resolution && (
                    <div className="text-sm font-semibold text-zinc-200">
                      {r.resolution}
                    </div>
                  )}
                  {r.credit_amount != null && (
                    <div className="font-mono-data text-sm text-indigo-300">
                      {rmaMoney(r.credit_amount)}
                    </div>
                  )}
                  <div className="mt-0.5 text-[10px] uppercase tracking-widest text-zinc-500">
                    {rmaDate(r.created_at)}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
